import I18n from 'I18n';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIStepperInput from 'UIComponents/input/UIStepperInput';
import { CALCULATOR } from 'pricing-pages-lib/constants/PurchaseMotionsLocations';
import { UNIT } from 'self-service-api/constants/UnitTypes';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { APP } from '../../constants/AuthType';
import { ADD_ON_CARD } from '../../constants/tracking/Locations';
import { useAppSelector } from '../../reducers';
import { activeCurrencySelector } from '../../selectors/CurrencySelectors';
import { checkoutProductConfigurationsSelector } from '../../selectors/ProductConfigsSelectors';
import { hasMaxProductQuantityInProductInventorySelector, hasProductVersionInProductInventorySelector, productsFromProductInventoryQuantitySelector } from '../../selectors/ProductInventorySelectors';
import { billingNameKeySelector, productForSkuIdSelector, productVersionSelector, upgradeProductSelector } from '../../selectors/ProductsSelectors';
import { purchaseMotionsSelector, talkToSalesTypeSelector } from '../../selectors/PurchaseMotionSelectors';
import { getBuyNowButtonClickEvent, getClickedAdditionalUnitsPickerEvent, getTalkToSalesEvent } from '../../utils/event-tracking/interactions';
import { trackEvent } from '../../utils/event-tracking/track-event';
import PurchaseMotionButtonWrapper from '../common/purchaseMotions/PurchaseMotionButtonWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledStepper = styled(UIStepperInput).withConfig({
  displayName: "AddOnCardCTA__StyledStepper",
  componentId: "sc-1v0ridi-0"
})(["width:100px;"]);
export const AddOnCardCTA = ({
  productConfigurations,
  screen,
  selectedQuantity,
  skuId,
  term,
  onQuantityChange
}) => {
  const checkoutProductConfigurations = useAppSelector(state => checkoutProductConfigurationsSelector(state, {
    productConfigurations
  }));
  const ownedQuantity = useAppSelector(state => productsFromProductInventoryQuantitySelector(state, {
    skuId
  }));

  // set the quantity to the owned quantity if it's less than the owned quantity
  useEffect(() => {
    if (selectedQuantity < ownedQuantity) {
      onQuantityChange(ownedQuantity);
    }
  }, [ownedQuantity, onQuantityChange, selectedQuantity]);
  const billingNameKey = useAppSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  const currencyCode = useSelector(activeCurrencySelector);
  const product = useAppSelector(state => productForSkuIdSelector(state, {
    skuId
  }));
  const quantifiable = useAppSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return productVersion.quantifiable;
  });
  const ownsMaxQuantityOfProduct = useAppSelector(state => hasMaxProductQuantityInProductInventorySelector(state, {
    skuId
  }));
  const maxTouchlessPurchasableQuantity = useAppSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return productVersion.maxTouchlessPurchasableQuantity;
  });
  const ownsProduct = useAppSelector(state => hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const limit = maxTouchlessPurchasableQuantity === null ? Infinity : maxTouchlessPurchasableQuantity || 1;
  const canBuyMultiple = quantifiable && limit > 1;
  const sourceId = 'add-on-card';
  const upgradeProduct = useAppSelector(state => upgradeProductSelector(state, {
    skuIds: checkoutProductConfigurations.map(config => config.skuId)
  }));
  const purchaseMotions = useAppSelector(state => {
    const motions = purchaseMotionsSelector(state, {
      skuId,
      location: CALCULATOR
    });

    // we want to always fallback to showing something if there are no motions or
    // if the user owns the max quantity of the product, something is better than nothing
    if (motions.length === 0 || ownsMaxQuantityOfProduct) {
      return [talkToSalesTypeSelector(state)];
    }
    return motions;
  });
  const isTouchlessPurchase = purchaseMotions.includes(BUY_TOUCHLESS);
  const handleButtonClick = () => {
    if (isTouchlessPurchase) {
      trackEvent(getBuyNowButtonClickEvent({
        productNames: product ? [product.apiName] : [],
        ownedProducts: ownsProduct && product ? [product.apiName] : []
      }));
    } else {
      trackEvent(getTalkToSalesEvent({
        productNames: product ? [product.apiName] : [],
        ownedProducts: ownsProduct && product ? [product.apiName] : []
      }));
    }
  };
  const handleStepperChange = evt => {
    const newQuantity = parseInt(evt.target.value, 10);
    if (isNaN(newQuantity)) return;
    const direction = newQuantity > selectedQuantity ? 'increase' : 'decrease';
    trackEvent(getClickedAdditionalUnitsPickerEvent({
      location: ADD_ON_CARD,
      action: `stepper ${direction}`,
      ownedUnitQuantity: ownedQuantity,
      displayedUnitQuantity: newQuantity,
      productName: product ? product.apiName : '',
      skuId,
      ownsProduct,
      unitType: UNIT
    }));
    onQuantityChange(newQuantity);
  };

  // disable the button if not touchless or the user hasn't increased the stepper to more than already owned
  const disabledButton = isTouchlessPurchase &&
  // only disable if touchless purchase is allowed (always let talk to sales be clickable)
  selectedQuantity <= ownedQuantity;
  const upgradeData = {
    apiName: product ? product.apiName : undefined,
    app: APP,
    screen,
    uniqueId: sourceId,
    upgradeProduct
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [canBuyMultiple && /*#__PURE__*/_jsx(UIFormControl, {
      "aria-label": I18n.text(`pricing-pages.products.${billingNameKey}.title`),
      children: /*#__PURE__*/_jsx(StyledStepper, {
        disabled: ownsMaxQuantityOfProduct,
        stepSize: 1,
        onChange: handleStepperChange,
        value: selectedQuantity,
        min: ownedQuantity || 1,
        max: limit
      })
    }), /*#__PURE__*/_jsx(PurchaseMotionButtonWrapper, {
      queryParamTerm: term,
      use: "primary",
      level: 1,
      trackingSource: sourceId,
      currencyCode: currencyCode,
      upgradeData: upgradeData,
      purchaseMotions: purchaseMotions,
      productConfigurations: checkoutProductConfigurations,
      disabled: disabledButton,
      onClick: handleButtonClick,
      location: ADD_ON_CARD
    })]
  });
};